import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InternalNavbar from "../../components/feature/InternalNavbar";

const valuesEs = [
  { icon: "ri-phone-line", title: "Cero llamadas perdidas", desc: "Cada llamada que no se responde es un cliente que se va. Eso es inaceptable para nosotros y para ti." },
  { icon: "ri-shield-check-line", title: "Confianza primero", desc: "Nunca vendemos datos. Nunca. Toda la infraestructura está en la UE y cumple el RGPD al 100%." },
  { icon: "ri-lightbulb-line", title: "Simplicidad radical", desc: "Si un negocio pequeño no puede configurarlo en 15 minutos, volvemos a diseñarlo desde cero." },
  { icon: "ri-global-line", title: "31 idiomas, 1 voz", desc: "Cada cliente merece ser atendido en su idioma. Auria lo hace posible sin coste adicional." },
];

const valuesEn = [
  { icon: "ri-phone-line", title: "Zero missed calls", desc: "Every unanswered call is a customer who leaves. That's unacceptable for us and for you." },
  { icon: "ri-shield-check-line", title: "Trust first", desc: "We never sell data. Ever. All infrastructure is in the EU and fully GDPR compliant." },
  { icon: "ri-lightbulb-line", title: "Radical simplicity", desc: "If a small business can't configure it in 15 minutes, we go back and redesign from scratch." },
  { icon: "ri-global-line", title: "31 languages, 1 voice", desc: "Every customer deserves to be served in their language. Auria makes that possible at no extra cost." },
];

export default function SobreNosotrosPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const values = isEn ? valuesEn : valuesEs;

  const navLinks = [
    { label: "Blog", href: "/blog" },
    { label: "Roadmap", href: "/roadmap" },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <InternalNavbar links={navLinks} rightCta={{ label: isEn ? "See plans" : "Ver planes", href: "/#precios" }} />
      <main className="flex-1">
        <section className="relative overflow-hidden py-24 px-6" style={{ background: "#0C0C0E" }}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(ellipse 80% 60% at 50% -10%, #f59e0b 0%, transparent 70%)" }} />
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-400 mb-5">{isEn ? "About us" : "Sobre nosotros"}</span>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "We build the receptionist every business deserves" : "Construimos la recepcionista que todo negocio merece"}
            </h1>
            <p className="text-stone-400 text-lg leading-relaxed max-w-xl mx-auto">
              {isEn ? "We're a small team with a big mission: making sure no business ever misses an important call again." : "Somos un equipo pequeño con una misión grande: asegurarnos de que ningún negocio pierda una llamada importante nunca más."}
            </p>
          </div>
        </section>

        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">{isEn ? "Our mission" : "Nuestra misión"}</span>
              <h2 className="text-3xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isEn ? "Every call is an opportunity. It should never be missed." : "Cada llamada es una oportunidad. Nunca debería perderse."}
              </h2>
              {isEn ? (
                <>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">Auria was born from a real frustration: businesses losing dozens of calls a day, customers going to the competition simply because nobody picked up the phone. Not for lack of desire, but because one human can't be everywhere.</p>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">We believe AI shouldn't be just for large corporations. A family restaurant, a dental clinic, an insurance agency — they all deserve the same responsiveness as a Fortune 500 company.</p>
                  <p className="text-stone-600 text-sm leading-relaxed">That's why we built Auria: an AI receptionist that sounds human, speaks 31 languages, never gets tired and costs less than an hour of work.</p>
                </>
              ) : (
                <>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">Auria nació de una frustración real: negocios con decenas de llamadas perdidas al día, clientes que se iban a la competencia simplemente porque nadie cogía el teléfono. No por falta de ganas, sino porque un solo humano no puede estar en todas partes.</p>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">Creemos que la IA no debería ser solo para las grandes corporaciones. Un restaurante familiar, una clínica dental, una agencia de seguros — todos merecen la misma capacidad de respuesta que una empresa del Fortune 500.</p>
                  <p className="text-stone-600 text-sm leading-relaxed">Por eso construimos Auria: una recepcionista de IA que suena humana, habla 31 idiomas, nunca se cansa y cuesta menos que una hora de trabajo.</p>
                </>
              )}
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/953fe91319a322c5b093c986bc630a44.jpg" alt={isEn ? "Auria Team" : "Equipo Auria"} className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-amber-500 text-white rounded-2xl px-5 py-4 text-center">
                <p className="text-2xl font-bold">+2.400</p>
                <p className="text-xs font-medium opacity-90">{isEn ? "businesses trust Auria" : "negocios confían en Auria"}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-6" style={{ background: "#FAFAF9" }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">{isEn ? "Our values" : "Nuestros valores"}</span>
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{isEn ? "What guides us every day" : "Lo que nos guía cada día"}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-stone-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 mb-4"><i className={`${v.icon} text-amber-600 text-lg`} /></div>
                  <h3 className="font-bold text-stone-900 text-sm mb-2">{v.title}</h3>
                  <p className="text-stone-500 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-6" style={{ background: "#0C0C0E" }}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "Ready to stop missing calls?" : "¿Listo para no perder más llamadas?"}
            </h2>
            <p className="text-stone-400 text-sm mb-8">{isEn ? "Join over 2,400 businesses already using Auria. 15-minute setup, no credit card." : "Únete a más de 2.400 negocios que ya usan Auria. Configuración en 15 minutos, sin tarjeta de crédito."}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#precios" className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm">{isEn ? "See plans and pricing" : "Ver planes y precios"}</Link>
              <a href="mailto:auriareceptionistai@gmail.com" className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm border border-white/10">{isEn ? "Contact the team" : "Contactar con el equipo"}</a>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-stone-50 border-t border-stone-100 px-6 py-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-400 text-xs">{isEn ? "© 2026 Auria. All rights reserved." : "© 2026 Auria. Todos los derechos reservados."}</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Home" : "Inicio"}</Link>
            <Link to="/blog" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">Blog</Link>
            <Link to="/roadmap" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">Roadmap</Link>
            <Link to="/privacidad" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Privacy" : "Privacidad"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
